import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import sharedStyles from './AdminShared.module.scss';
import styles from './AdminDiagnostics.module.scss';
import AutoRefreshControl from './components/AutoRefreshControl';
import { DiagnosticsDeploymentDto, DiagnosticsSnapshotDto } from '../../../types/admin.types';
import { BASE_URL, Routes } from '../../../types/constants';
import { fetchAdminDiagnostics } from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';
import { isSuperAdmin } from '../../../utils/roles';
import { useAutoRefresh } from '../../../hooks/useAutoRefresh';

type DiagnosticsTab = 'overview' | 'deployment' | 'api-db' | 'frontend' | 'session' | 'integrations' | 'network';

interface NetworkCheck {
    name: string;
    url: string;
    ok: boolean;
    status?: number;
    responseTimeMs: number;
    checkedAtUtc: string;
    error?: string;
}

interface SessionDiagnostics {
    username?: string | null;
    roles: string[];
    tokenPresent: boolean;
    tokenExpiresAtUtc?: string | null;
    tokenSecondsRemaining?: number | null;
}

const tabs: { id: DiagnosticsTab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'deployment', label: 'Deployment' },
    { id: 'api-db', label: 'API & DB' },
    { id: 'frontend', label: 'Frontend Runtime' },
    { id: 'session', label: 'Session/Auth' },
    { id: 'integrations', label: 'Integrations' },
    { id: 'network', label: 'Network Checks' }
];

const emptyMetadata: DiagnosticsDeploymentDto = {};

const formatDateTime = (value?: string | null) => {
    if (!value) {
        return '-';
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const formatDuration = (seconds?: number | null) => {
    if (seconds === undefined || seconds === null) {
        return '-';
    }

    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (days > 0) {
        return `${days}d ${hours}h ${minutes}m`;
    }

    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }

    return `${minutes}m`;
};

const formatBool = (value?: boolean | null) => value ? 'Yes' : 'No';

const formatWorkerMode = (value?: number | null) => {
    if (value === 0) {
        return 'Standard';
    }

    if (value === 1) {
        return 'Mobile Backfill';
    }

    return 'Not configured';
};

const formatOutputFormat = (value?: number | null) => {
    if (value === 2) {
        return 'MP4';
    }

    if (value === 3) {
        return 'Mobile MP4';
    }

    if (value === 1) {
        return 'WebM';
    }

    if (value === 0) {
        return 'Unprocessed';
    }

    return '-';
};

const statusClass = (ok?: boolean | null, warning = false) => {
    if (warning) {
        return styles.statusWarn;
    }

    return ok ? styles.statusGood : styles.statusBad;
};

const readMetadata = async (url: string): Promise<DiagnosticsDeploymentDto> => {
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) {
        throw new Error(`Unable to load ${url}`);
    }

    return response.json() as Promise<DiagnosticsDeploymentDto>;
};

const runNetworkCheck = async (name: string, url: string, token?: string | null): Promise<NetworkCheck> => {
    const started = performance.now();
    try {
        const response = await fetch(url, {
            cache: 'no-store',
            headers: token ? { Authorization: `Bearer ${token}` } : undefined
        });
        await response.text().catch(() => '');
        return {
            name,
            url,
            ok: response.ok,
            status: response.status,
            responseTimeMs: Math.round(performance.now() - started),
            checkedAtUtc: new Date().toISOString()
        };
    } catch (error) {
        return {
            name,
            url,
            ok: false,
            responseTimeMs: Math.round(performance.now() - started),
            checkedAtUtc: new Date().toISOString(),
            error: error instanceof Error ? error.message : 'Request failed.'
        };
    }
};

const decodeTokenExpiry = (token?: string | null) => {
    if (!token) {
        return { tokenExpiresAtUtc: null, tokenSecondsRemaining: null };
    }

    try {
        const payload = token.split('.')[1];
        if (!payload) {
            return { tokenExpiresAtUtc: null, tokenSecondsRemaining: null };
        }

        const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
        const parsed = JSON.parse(window.atob(normalized));
        if (typeof parsed.exp !== 'number') {
            return { tokenExpiresAtUtc: null, tokenSecondsRemaining: null };
        }

        const expiresAtMs = parsed.exp * 1000;
        return {
            tokenExpiresAtUtc: new Date(expiresAtMs).toISOString(),
            tokenSecondsRemaining: Math.max(0, Math.floor((expiresAtMs - Date.now()) / 1000))
        };
    } catch {
        return { tokenExpiresAtUtc: null, tokenSecondsRemaining: null };
    }
};

const Detail = ({ label, value, mono = false }: { label: string; value?: React.ReactNode; mono?: boolean }) => (
    <div className={styles.keyValue}>
        <div className={styles.label}>{label}</div>
        <div className={`${styles.value} ${mono ? styles.mono : ''}`}>{value ?? '-'}</div>
    </div>
);

const AdminDiagnostics = () => {
    const { getUserInfo } = useUserState();
    const user = getUserInfo();
    const token = user?.token;
    const canView = isSuperAdmin(user?.roles);
    const [activeTab, setActiveTab] = useState<DiagnosticsTab>('overview');
    const [diagnostics, setDiagnostics] = useState<DiagnosticsSnapshotDto | null>(null);
    const [frontendBuild, setFrontendBuild] = useState<DiagnosticsDeploymentDto>(emptyMetadata);
    const [frontendRuntime, setFrontendRuntime] = useState<DiagnosticsDeploymentDto>(emptyMetadata);
    const [networkChecks, setNetworkChecks] = useState<NetworkCheck[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [autoRefresh, setAutoRefresh] = useState(false);

    const sessionDiagnostics = useMemo<SessionDiagnostics>(() => {
        const expiry = decodeTokenExpiry(token);
        return {
            username: user?.username,
            roles: user?.roles ?? [],
            tokenPresent: Boolean(token),
            ...expiry
        };
    }, [token, user?.roles, user?.username]);

    const loadDiagnostics = useCallback(async () => {
        if (!canView) {
            return;
        }

        setIsLoading(true);
        setErrorMessage(null);

        const adminStarted = performance.now();
        const [backendResult, buildResult, runtimeResult, pingCheck, siteInfoCheck] = await Promise.allSettled([
            fetchAdminDiagnostics(token),
            readMetadata('/diagnostics/build.json'),
            readMetadata('/diagnostics/runtime.json'),
            runNetworkCheck('API ping', `${BASE_URL}/ping`),
            runNetworkCheck('Public site config', `${BASE_URL}/v1/site/site-info`)
        ]);

        if (backendResult.status === 'fulfilled') {
            setDiagnostics(backendResult.value);
            setNetworkChecks([
                {
                    name: 'Admin diagnostics API',
                    url: `${BASE_URL}/v1/admin/diagnostics`,
                    ok: true,
                    status: 200,
                    responseTimeMs: Math.round(performance.now() - adminStarted),
                    checkedAtUtc: new Date().toISOString()
                },
                pingCheck.status === 'fulfilled' ? pingCheck.value : buildRejectedNetworkCheck('API ping', `${BASE_URL}/ping`, pingCheck.reason),
                siteInfoCheck.status === 'fulfilled' ? siteInfoCheck.value : buildRejectedNetworkCheck('Public site config', `${BASE_URL}/v1/site/site-info`, siteInfoCheck.reason)
            ]);
        } else {
            setErrorMessage(backendResult.reason instanceof Error ? backendResult.reason.message : 'Unable to load diagnostics.');
            setNetworkChecks([
                {
                    name: 'Admin diagnostics API',
                    url: `${BASE_URL}/v1/admin/diagnostics`,
                    ok: false,
                    responseTimeMs: Math.round(performance.now() - adminStarted),
                    checkedAtUtc: new Date().toISOString(),
                    error: backendResult.reason instanceof Error ? backendResult.reason.message : 'Request failed.'
                },
                pingCheck.status === 'fulfilled' ? pingCheck.value : buildRejectedNetworkCheck('API ping', `${BASE_URL}/ping`, pingCheck.reason),
                siteInfoCheck.status === 'fulfilled' ? siteInfoCheck.value : buildRejectedNetworkCheck('Public site config', `${BASE_URL}/v1/site/site-info`, siteInfoCheck.reason)
            ]);
        }

        setFrontendBuild(buildResult.status === 'fulfilled' ? buildResult.value : emptyMetadata);
        setFrontendRuntime(runtimeResult.status === 'fulfilled' ? runtimeResult.value : emptyMetadata);
        setIsLoading(false);
    }, [canView, token]);

    useEffect(() => {
        loadDiagnostics();
    }, [loadDiagnostics]);

    useAutoRefresh(autoRefresh, loadDiagnostics);

    if (!canView) {
        return <Navigate to={Routes.ADMIN_ANALYTICS} replace />;
    }

    const db = diagnostics?.Database;
    const deployment = diagnostics?.Deployment;
    const contentProcessor = diagnostics?.ContentProcessor;
    const browserOnline = typeof navigator === 'undefined' ? true : navigator.onLine;

    return (
        <div className={sharedStyles.page}>
            <div className={sharedStyles.header}>
                <div>
                    <h1>Diagnostics</h1>
                    <p>Deployment, runtime, API, database, and browser checks for SuperAdministrator review.</p>
                </div>
                <div className={styles.actions}>
                    <AutoRefreshControl checked={autoRefresh} onChange={setAutoRefresh} />
                    <button type="button" className={sharedStyles.secondary} onClick={loadDiagnostics} disabled={isLoading}>
                        {isLoading ? 'Checking...' : 'Refresh'}
                    </button>
                </div>
            </div>

            {errorMessage ? <div className={sharedStyles.readOnlyBlock}>{errorMessage}</div> : null}

            <div className={styles.tabs} role="tablist" aria-label="Diagnostics sections">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab.id}
                        className={activeTab === tab.id ? `${styles.tab} ${styles.tabActive}` : styles.tab}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className={styles.tabPanel}>
                {activeTab === 'overview' ? (
                    <>
                        <div className={styles.grid}>
                            <StatusCard title="Overall" ok={diagnostics?.Status === 'Healthy'} value={diagnostics?.Status ?? 'Unknown'} />
                            <StatusCard title="API" ok={Boolean(diagnostics)} value={diagnostics ? `${diagnostics.ResponseGeneratedInMs} ms` : 'Unavailable'} />
                            <StatusCard title="Database" ok={db?.CanConnect} warning={db?.Status === 'Degraded'} value={db?.Status ?? 'Unknown'} />
                            <StatusCard title="Browser" ok={browserOnline} value={browserOnline ? 'Online' : 'Offline'} />
                        </div>
                        <div className={styles.wideGrid}>
                            <div className={styles.card}>
                                <h3>Current targets</h3>
                                <div className={styles.keyValueList}>
                                    <Detail label="Frontend origin" value={window.location.origin} mono />
                                    <Detail label="Current API URL" value={BASE_URL} mono />
                                    <Detail label="Backend configured API URL" value={diagnostics?.Api.ConfiguredApiUrl} mono />
                                    <Detail label="Last checked" value={formatDateTime(diagnostics?.CheckedAtUtc)} />
                                </div>
                            </div>
                            <div className={styles.card}>
                                <h3>Warnings</h3>
                                {diagnostics?.Warnings?.length ? (
                                    <ul className={styles.warningList}>
                                        {diagnostics.Warnings.map((warning) => <li key={warning}>{warning}</li>)}
                                    </ul>
                                ) : (
                                    <div className={sharedStyles.muted}>No warnings reported.</div>
                                )}
                            </div>
                        </div>
                    </>
                ) : null}

                {activeTab === 'deployment' ? (
                    <div className={styles.wideGrid}>
                        <DeploymentCard title="Frontend build" deployment={frontendBuild} />
                        <DeploymentCard title="Frontend runtime" deployment={frontendRuntime} />
                        <DeploymentCard title="Backend runtime" deployment={deployment ?? emptyMetadata} />
                    </div>
                ) : null}

                {activeTab === 'api-db' ? (
                    <div className={styles.wideGrid}>
                        <div className={styles.card}>
                            <h3>API</h3>
                            <div className={styles.keyValueList}>
                                <Detail label="Environment" value={diagnostics?.Api.Environment} />
                                <Detail label="Machine" value={diagnostics?.Api.MachineName} />
                                <Detail label="Request URL" value={diagnostics?.Api.RequestUrl} mono />
                                <Detail label="Started" value={formatDateTime(diagnostics?.Api.StartedAtUtc)} />
                                <Detail label="Uptime" value={formatDuration(diagnostics?.Api.UptimeSeconds)} />
                                <Detail label="Allowed origins" value={diagnostics?.Api.AllowedOrigins?.join(', ') || '-'} mono />
                            </div>
                        </div>
                        <div className={styles.card}>
                            <h3>Database</h3>
                            <div className={styles.keyValueList}>
                                <Detail label="Status" value={db?.Status} />
                                <Detail label="API to DB response" value={db?.ResponseTimeMs === undefined || db.ResponseTimeMs === null ? '-' : `${db.ResponseTimeMs} ms`} />
                                <Detail label="Provider" value={db?.ProviderName} mono />
                                <Detail label="Connection source" value={db?.ConnectionSource} mono />
                                <Detail label="Data source" value={db?.DataSource} mono />
                                <Detail label="Database" value={db?.DatabaseName} mono />
                                <Detail label="Latest migration" value={db?.LatestAppliedMigration} mono />
                                <Detail label="Pending migrations" value={db?.PendingMigrationCount?.toString()} />
                                <Detail label="Run migrations on startup" value={formatBool(db?.RunMigrationsOnStartup)} />
                                <Detail label="Migration timeout" value={db?.MigrationCommandTimeoutSeconds ? `${db.MigrationCommandTimeoutSeconds}s` : '-'} />
                                {db?.Error ? <Detail label="Error" value={db.Error} /> : null}
                            </div>
                        </div>
                    </div>
                ) : null}

                {activeTab === 'frontend' ? (
                    <div className={styles.wideGrid}>
                        <div className={styles.card}>
                            <h3>Browser</h3>
                            <div className={styles.keyValueList}>
                                <Detail label="Origin" value={window.location.origin} mono />
                                <Detail label="Path" value={`${window.location.pathname}${window.location.search}`} mono />
                                <Detail label="Timezone" value={Intl.DateTimeFormat().resolvedOptions().timeZone} />
                                <Detail label="Online" value={formatBool(browserOnline)} />
                                <Detail label="User agent" value={navigator.userAgent} />
                            </div>
                        </div>
                        <div className={styles.card}>
                            <h3>Storage and tags</h3>
                            <div className={styles.keyValueList}>
                                <Detail label="localStorage" value={formatBool(hasStorage('localStorage'))} />
                                <Detail label="sessionStorage" value={formatBool(hasStorage('sessionStorage'))} />
                                <Detail label="Marketing consent" value={readStorageValue('combattv_marketing_consent')} />
                                <Detail label="dataLayer loaded" value={formatBool(Boolean(window.dataLayer))} />
                                <Detail label="gtag loaded" value={formatBool(Boolean(window.gtag))} />
                                <Detail label="Meta Pixel loaded" value={formatBool(Boolean(window.fbq))} />
                            </div>
                        </div>
                    </div>
                ) : null}

                {activeTab === 'session' ? (
                    <div className={styles.wideGrid}>
                        <div className={styles.card}>
                            <h3>Current user</h3>
                            <div className={styles.keyValueList}>
                                <Detail label="Username" value={sessionDiagnostics.username} />
                                <Detail label="Roles" value={sessionDiagnostics.roles.join(', ') || '-'} />
                                <Detail label="Token present" value={formatBool(sessionDiagnostics.tokenPresent)} />
                                <Detail label="Token expiry" value={formatDateTime(sessionDiagnostics.tokenExpiresAtUtc)} />
                                <Detail label="Token time remaining" value={formatDuration(sessionDiagnostics.tokenSecondsRemaining)} />
                            </div>
                        </div>
                    </div>
                ) : null}

                {activeTab === 'integrations' ? (
                    <div className={styles.wideGrid}>
                        {diagnostics?.Integrations.map((integration) => (
                            <div key={integration.Name} className={styles.card}>
                                <h3>{integration.Name}</h3>
                                <span className={statusClass(integration.IsConfigured)}>
                                    {integration.IsConfigured ? 'Configured' : 'Missing config'}
                                </span>
                                <p className={sharedStyles.muted}>{integration.Detail}</p>
                            </div>
                        ))}
                        <div className={styles.card}>
                            <h3>Content processor</h3>
                            <div className={styles.keyValueList}>
                                <Detail label="Processing enabled" value={formatBool(contentProcessor?.IsPollingEnabled)} />
                                <Detail label="Idle interval" value={contentProcessor ? `${contentProcessor.PollingIntervalSeconds}s` : '-'} />
                                <Detail label="Paused until" value={formatDateTime(contentProcessor?.PauseUntilUtc)} />
                                <Detail label="Last worker" value={contentProcessor?.LastWorkerId} />
                                <Detail label="Last heartbeat" value={formatDateTime(contentProcessor?.LastWorkerHeartbeatUtc)} />
                                <Detail label="Registered workers" value={(contentProcessor?.Workers?.length ?? 0).toString()} />
                                <Detail label="Events last 7 days" value={contentProcessor?.TotalEventsLast7Days?.toString()} />
                                <Detail label="Errors last 7 days" value={contentProcessor?.ErrorsLast7Days?.toString()} />
                                {contentProcessor?.Error ? <Detail label="Error" value={contentProcessor.Error} /> : null}
                            </div>
                        </div>
                        {(contentProcessor?.Workers ?? []).map((worker) => (
                            <div key={worker.WorkerId} className={styles.card}>
                                <h3>{worker.DisplayName || worker.WorkerId}</h3>
                                <span className={statusClass(worker.EffectiveIsPollingEnabled, worker.ErrorsLast7Days > 0)}>
                                    {worker.EffectiveIsPollingEnabled ? 'Enabled' : 'Disabled'}
                                </span>
                                <div className={styles.keyValueList}>
                                    <Detail label="Worker ID" value={worker.WorkerId} mono />
                                    <Detail label="Enabled" value={formatBool(worker.IsEnabled)} />
                                    <Detail label="Configured" value={formatBool(worker.IsConfigured)} />
                                    <Detail label="Can process work" value={formatBool(worker.CanProcessWork)} />
                                    <Detail label="Status" value={worker.ConfigurationStatusMessage ?? worker.ConfigurationStatus} />
                                    <Detail label="Mode" value={formatWorkerMode(worker.WorkerMode)} />
                                    <Detail label="Output" value={formatOutputFormat(worker.OutputFormat)} />
                                    <Detail label="Template" value={worker.AssignedTranscodeTemplateName} />
                                    <Detail label="Effective interval" value={`${worker.EffectivePollingIntervalSeconds}s`} />
                                    <Detail label="Paused until" value={formatDateTime(worker.EffectivePauseUntilUtc)} />
                                    <Detail label="Last seen" value={formatDateTime(worker.LastSeenUtc)} />
                                    <Detail label="Last config poll" value={formatDateTime(worker.LastConfigurationPollUtc)} />
                                    <Detail label="Last event" value={formatDateTime(worker.LastEventUtc)} />
                                    <Detail label="Last log" value={formatDateTime(worker.LastLogUtc)} />
                                    <Detail label="Last error" value={formatDateTime(worker.LastErrorUtc)} />
                                    <Detail label="Jobs completed last 7 days" value={worker.JobsCompletedLast7Days.toString()} />
                                    <Detail label="Jobs failed last 7 days" value={worker.JobsFailedLast7Days.toString()} />
                                    <Detail label="Errors last 7 days" value={worker.ErrorsLast7Days.toString()} />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : null}

                {activeTab === 'network' ? (
                    <div className={styles.card}>
                        <h3>Browser to service checks</h3>
                        {networkChecks.map((check) => (
                            <div key={check.name} className={styles.networkRow}>
                                <div>
                                    <div className={styles.value}>{check.name}</div>
                                    <div className={`${sharedStyles.muted} ${styles.mono}`}>{check.url}</div>
                                </div>
                                <span className={statusClass(check.ok)}>{check.ok ? 'OK' : 'Failed'}</span>
                                <div>{check.status ?? '-'}</div>
                                <div>{check.responseTimeMs} ms {check.error ? `- ${check.error}` : ''}</div>
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

const buildRejectedNetworkCheck = (name: string, url: string, reason: unknown): NetworkCheck => ({
    name,
    url,
    ok: false,
    responseTimeMs: 0,
    checkedAtUtc: new Date().toISOString(),
    error: reason instanceof Error ? reason.message : 'Request failed.'
});

const StatusCard = ({ title, ok, value, warning = false }: { title: string; ok?: boolean | null; value: string; warning?: boolean }) => (
    <div className={styles.card}>
        <div className={styles.label}>{title}</div>
        <div className={styles.metric}>{value}</div>
        <span className={statusClass(ok, warning)}>{warning ? 'Warning' : ok ? 'OK' : 'Check'}</span>
    </div>
);

const DeploymentCard = ({ title, deployment }: { title: string; deployment: DiagnosticsDeploymentDto }) => (
    <div className={styles.card}>
        <h3>{title}</h3>
        <div className={styles.keyValueList}>
            <Detail label="Commit" value={deployment.ShortCommitSha ?? deployment.CommitSha} mono />
            <Detail label="Full SHA" value={deployment.CommitSha} mono />
            <Detail label="Image tag" value={deployment.ImageTag} mono />
            <Detail label="Build number" value={deployment.BuildNumber} />
            <Detail label="Run id" value={deployment.RunId} mono />
            <Detail label="Run attempt" value={deployment.RunAttempt} />
            <Detail label="Build date" value={formatDateTime(deployment.BuildDateUtc)} />
            <Detail label="Deployed date" value={formatDateTime(deployment.DeployedAtUtc)} />
            <Detail label="Repository" value={deployment.Repository} mono />
            <Detail label="Workflow" value={deployment.Workflow} />
        </div>
    </div>
);

const hasStorage = (storageName: 'localStorage' | 'sessionStorage') => {
    try {
        const storage = window[storageName];
        const key = '__combattv_diagnostics_test__';
        storage.setItem(key, key);
        storage.removeItem(key);
        return true;
    } catch {
        return false;
    }
};

const readStorageValue = (key: string) => {
    try {
        return window.localStorage.getItem(key) ?? '-';
    } catch {
        return 'Unavailable';
    }
};

export default AdminDiagnostics;
